import React, { useEffect, useRef, useState } from "react";
import { Type, Edit3, Sparkles, RotateCcw } from "lucide-react";

const NAME_FONTS = [
  { label: "Great Vibes", css: "'Great Vibes', cursive" },
  { label: "Dancing Script", css: "'Dancing Script', cursive" },
  { label: "Homemade Apple", css: "'Homemade Apple', cursive" },
  { label: "Sacramento", css: "'Sacramento', cursive" },
  { label: "Alex Brush", css: "'Alex Brush', cursive" },
  { label: "Allura", css: "'Allura', cursive" },
  { label: "Kaushan Script", css: "'Kaushan Script', cursive" },
  { label: "Satisfy", css: "'Satisfy', cursive" },
];

const FONT_LINK_ID = "venturaz-esign-fonts";
function ensureFonts() {
  if (typeof document === "undefined") return;
  if (document.getElementById(FONT_LINK_ID)) return;
  const l = document.createElement("link");
  l.id = FONT_LINK_ID;
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Great+Vibes&family=Dancing+Script:wght@600&family=Homemade+Apple&family=Sacramento&family=Alex+Brush&family=Allura&family=Kaushan+Script&family=Satisfy&display=swap";
  document.head.appendChild(l);
}

const Tab = ({ id, label, icon: Icon, active, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(id)}
    data-testid={`sig-tab-${id}`}
    className={`flex-1 flex items-center justify-center gap-2 py-3 text-xs font-mono uppercase tracking-widest transition-colors border-b-2 ${
      active ? "border-gold text-gold" : "border-transparent text-ink-muted hover:text-ink"
    }`}
  >
    <Icon size={14} /> {label}
  </button>
);

export default function SignatureCapture({ signerName = "", onSign, submitting }) {
  const [tab, setTab] = useState("style");
  const [typedValue, setTypedValue] = useState(signerName);
  const [nameFont, setNameFont] = useState(NAME_FONTS[0].label);
  const canvasRef = useRef(null);
  const drawingRef = useRef(false);
  const [hasDraw, setHasDraw] = useState(false);

  useEffect(() => { ensureFonts(); }, []);

  const clearCanvas = () => {
    const c = canvasRef.current;
    if (!c) return;
    const ctx = c.getContext("2d");
    ctx.clearRect(0, 0, c.width, c.height);
    setHasDraw(false);
  };

  const startDraw = (e) => {
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    ctx.strokeStyle = "#0F3D2E";
    ctx.lineWidth = 2.2;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.beginPath();
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    ctx.moveTo(x, y);
    drawingRef.current = true;
  };
  const moveDraw = (e) => {
    if (!drawingRef.current) return;
    const c = canvasRef.current;
    const rect = c.getBoundingClientRect();
    const ctx = c.getContext("2d");
    const x = (e.touches?.[0]?.clientX ?? e.clientX) - rect.left;
    const y = (e.touches?.[0]?.clientY ?? e.clientY) - rect.top;
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasDraw(true);
    if (e.cancelable) e.preventDefault();
  };
  const endDraw = () => { drawingRef.current = false; };

  const submit = () => {
    if (tab === "type") {
      if (!typedValue.trim()) return;
      onSign({ signature_type: "typed", value: typedValue.trim() });
    } else if (tab === "style") {
      if (!typedValue.trim()) return;
      onSign({ signature_type: "name-style", value: typedValue.trim(), font: nameFont });
    } else if (tab === "draw") {
      if (!hasDraw) return;
      const dataUrl = canvasRef.current.toDataURL("image/png");
      onSign({ signature_type: "drawn", value: dataUrl });
    }
  };

  return (
    <div className="border border-white/10 bg-white/80 backdrop-blur rounded-md" data-testid="signature-capture">
      <div className="flex border-b border-white/10">
        <Tab id="style" label="Style Name" icon={Sparkles} active={tab === "style"} onClick={setTab} />
        <Tab id="type" label="Type" icon={Type} active={tab === "type"} onClick={setTab} />
        <Tab id="draw" label="Draw" icon={Edit3} active={tab === "draw"} onClick={setTab} />
      </div>

      <div className="p-6">
        {tab === "style" && (
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-2 block">Full Name</label>
            <input
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              className="w-full px-3 py-2 border border-ink-line/40 bg-white text-ink text-sm mb-4"
              placeholder="Enter your legal name"
              data-testid="sig-style-name-input"
            />
            <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-2 block">Signature Style</label>
            <select
              value={nameFont}
              onChange={(e) => setNameFont(e.target.value)}
              className="w-full px-3 py-2 border border-ink-line/40 bg-white text-ink text-sm mb-4"
              data-testid="sig-style-font-select"
            >
              {NAME_FONTS.map((f) => (
                <option key={f.label} value={f.label}>{f.label}</option>
              ))}
            </select>
            <div className="border-2 border-dashed border-gold/40 bg-white h-24 flex items-center px-6" data-testid="sig-style-preview">
              <span
                style={{ fontFamily: NAME_FONTS.find((f) => f.label === nameFont)?.css, fontSize: "32px", color: "#0F3D2E" }}
              >
                {typedValue || "Your signature"}
              </span>
            </div>
          </div>
        )}

        {tab === "type" && (
          <div>
            <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted mb-2 block">Type your signature</label>
            <input
              value={typedValue}
              onChange={(e) => setTypedValue(e.target.value)}
              className="w-full px-3 py-2 border border-ink-line/40 bg-white text-ink text-sm mb-3"
              placeholder="Your full legal name"
              data-testid="sig-type-input"
            />
            <div className="border-2 border-dashed border-gold/40 bg-white h-24 flex items-center px-6">
              <span className="font-heading text-ink text-2xl">{typedValue || "Your signature"}</span>
            </div>
          </div>
        )}

        {tab === "draw" && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Draw with mouse or finger</label>
              <button type="button" onClick={clearCanvas} className="flex items-center gap-1 text-xs text-ink-muted hover:text-ink" data-testid="sig-draw-clear">
                <RotateCcw size={12} /> Clear
              </button>
            </div>
            <canvas
              ref={canvasRef}
              width={520}
              height={140}
              className="w-full border-2 border-dashed border-gold/40 bg-white touch-none rounded-sm"
              style={{ cursor: "crosshair" }}
              data-testid="sig-draw-canvas"
              onMouseDown={startDraw}
              onMouseMove={moveDraw}
              onMouseUp={endDraw}
              onMouseLeave={endDraw}
              onTouchStart={startDraw}
              onTouchMove={moveDraw}
              onTouchEnd={endDraw}
            />
          </div>
        )}

        <div className="mt-6 flex items-center justify-between">
          <div className="text-[11px] text-ink-muted">
            By signing, you agree that this electronic signature is legally binding under IT Act 2000 §5.
          </div>
          <button
            type="button"
            onClick={submit}
            disabled={submitting || (tab !== "draw" && !typedValue.trim()) || (tab === "draw" && !hasDraw)}
            className="bg-gold text-ink px-6 py-2 text-sm font-mono uppercase tracking-widest hover:bg-gold/90 disabled:opacity-40"
            data-testid="sig-submit-btn"
          >
            {submitting ? "Signing…" : "Apply Signature"}
          </button>
        </div>
      </div>
    </div>
  );
}
